import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';
import { Users, Home, Clock, CalendarDays, Plane, CheckCircle, Calendar, Receipt, Settings, Building2, ChevronRight, History, DollarSign, CalendarCheck } from 'lucide-react';
import { useState } from 'react';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  useSidebar,
} from '@/components/ui/sidebar';
import { useUserPermissions } from '@/hooks/permissions';
import { PermissionKey } from '@/types/roles';
import { BusinessSettingsMenu, useVisibleBusinessSettingsCount } from '@/components/business-settings/BusinessSettingsMenu';

/** Check if user has ANY of the given permissions */
const hasAny = (permissions: PermissionKey[], keys: PermissionKey[]): boolean =>
  keys.some(k => permissions.includes(k));

export function AppSidebar() {
  const { t } = useTranslation();
  const { setOpenMobile } = useSidebar();
  const location = useLocation();
  const [isBusinessSettingsExpanded, setIsBusinessSettingsExpanded] = useState(false);

  const { permissions } = useUserPermissions();
  const visibleBusinessSettingsCount = useVisibleBusinessSettingsCount();

  const canAccessAdmin = visibleBusinessSettingsCount > 0;

  // Navigation items for all users
  const userNavItems = [
    {
      title: t('nav.dashboard', 'Dashboard'),
      url: '/',
      icon: Home,
    },
    {
      title: t('nav.myTimesheets', 'My Timesheets'),
      url: '/timesheets',
      icon: Clock,
    },
    {
      title: t('nav.leave', 'Leave'),
      url: '/leave',
      icon: Plane,
    },
    {
      title: t('nav.mySchedule', 'My Schedule'),
      url: '/my-schedule',
      icon: Calendar,
    },
    {
      title: t('nav.payslips', 'My Payslips'),
      url: '/payslips',
      icon: Receipt,
    },
    {
      title: t('nav.settings', 'Settings'),
      url: '/settings',
      icon: Settings,
    },
  ];

  // Manager/Admin items — each gated by its route's exact permissions
  const managerNavItems: Array<typeof userNavItems[number] & { requiredPermissions: PermissionKey[] }> = [
    {
      title: t('nav.teamTimesheets', 'Team Timesheets'),
      url: '/manager/timesheets',
      icon: CalendarDays,
      requiredPermissions: ['approve_timesheets'],
    },
    {
      title: t('nav.attendanceActivity', 'Attendance Activity'),
      url: '/manager/attendance-activity',
      icon: CheckCircle,
      requiredPermissions: ['approve_timesheets'],
    },
    {
      title: t('nav.leaveApprovals', 'Leave Approvals'),
      url: '/manager/leave',
      icon: CheckCircle,
      requiredPermissions: ['approve_leave_requests'],
    },
    {
      title: t('nav.schedulePlanner', 'Schedule Planner'),
      url: '/manager/schedule',
      icon: Calendar,
      requiredPermissions: ['add_edit_shifts'],
    },
    {
      title: t('nav.rosterPlanner', 'Roster Planner'),
      url: '/manager/roster',
      icon: CalendarCheck,
      requiredPermissions: ['roster_manager'],
    },
    {
      title: t('nav.team', 'Team'),
      url: '/team',
      icon: Users,
      requiredPermissions: ['view_employee_details', 'manage_employee_details'],
    },
    {
      title: t('nav.history', 'History'),
      url: '/manager/history',
      icon: History,
      requiredPermissions: ['approve_leave_requests', 'approve_timesheets', 'roster_manager', 'add_edit_shifts', 'system_config_manage'],
    },
    {
      title: t('nav.payroll', 'Payroll'),
      url: '/manager/payroll',
      icon: DollarSign,
      requiredPermissions: ['timesheet_staging'],
    },
  ];

  const visibleManagerItems = managerNavItems.filter(item =>
    hasAny(permissions, item.requiredPermissions)
  );


  const getNavClassName = ({ isActive }: { isActive: boolean }) =>
    isActive 
      ? 'bg-accent text-accent-foreground font-medium' 
      : 'text-muted-foreground hover:text-foreground hover:bg-accent/50';

  const handleNavClick = () => {
    // Close mobile sidebar when navigating
    setOpenMobile(false);
  };

  const toggleBusinessSettings = () => {
    setIsBusinessSettingsExpanded(!isBusinessSettingsExpanded);
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarContent>
        {/* User Navigation */}
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {userNavItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink 
                      to={item.url} 
                      end={item.url === '/'}
                      className={getNavClassName}
                      onClick={handleNavClick}
                    >
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Manager Navigation — only shown if at least one item is visible */}
        {visibleManagerItems.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel>Management</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {visibleManagerItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild>
                      <NavLink 
                        to={item.url}
                        className={getNavClassName}
                        onClick={handleNavClick}
                      >
                        <item.icon className="h-4 w-4" />
                        <span>{item.title}</span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* Admin Navigation - Business Settings */}
        {canAccessAdmin && (
          <SidebarGroup>
            <SidebarGroupLabel>Administration</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton onClick={toggleBusinessSettings}>
                    <Building2 className="h-4 w-4" />
                    <span>Business Settings</span>
                    <ChevronRight 
                      className={`h-4 w-4 transition-transform ${
                        isBusinessSettingsExpanded ? 'rotate-90' : ''
                      }`} 
                    />
                  </SidebarMenuButton>
                  {isBusinessSettingsExpanded && (
                    <SidebarMenuSub>
                      <BusinessSettingsMenu />
                    </SidebarMenuSub>
                  )}
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>
    </Sidebar>
  );
}
